'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button, buttonVariants } from '@/components/ui/button';
import type { Product, Category, StoreSettings, AppearanceSettings, DeliverySettings } from '@/lib/types';
import { ArrowRight, ShoppingBag, Truck, Gift, Share2, Clock, Cigarette, BatteryCharging, Droplets, Wrench } from 'lucide-react';
import { ProductCard } from '@/components/shared/ProductCard';

interface HomePageContentProps {
  products: Product[];
  categories: Category[];
  storeSettings: StoreSettings | null;
  appearanceSettings: AppearanceSettings | null;
  deliverySettings: DeliverySettings | null;
  error: string | null;
}

const iconMap: { [key: string]: React.FC<any> } = {
  Cigarette,
  BatteryCharging,
  Droplets,
  Wrench,
  ShoppingBag, // Fallback
};

export function HomePageContent({
  products,
  categories,
  storeSettings,
  appearanceSettings,
  deliverySettings,
  error,
}: HomePageContentProps) {

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-4 text-center">
        <h2 className="text-2xl font-bold text-red-500 mb-2">Ops! Algo deu errado.</h2>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>Tentar Novamente</Button>
      </div>
    );
  }

  const mainCategories = categories; 
  const heroImageUrl = appearanceSettings?.heroBannerUrl || "https://images.unsplash.com/photo-1599335622431-cb35c9a093a2?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";
  const heroSubtitle = appearanceSettings?.heroSubtitle || 
`Entrega Grátis Para Toda João Pessoa!
Aberta Todos Os Dias das 9h às 23h30!
Receba no Mesmo Dia!`;

  return (
    <div className="flex flex-col min-h-screen">
      
      {/* Hero Section */}
      <section className="relative w-full h-[70vh] md:h-[80vh] lg:h-[90vh] overflow-hidden flex items-center justify-center text-center text-white">
        <Image
          src={heroImageUrl}
          alt="Lobo"
          fill
          className="object-cover object-center"
          priority
          data-ai-hint="wolf face"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 p-4 animate-fade-in-down">
          <h1 className="text-5xl md:text-7xl font-bold font-headline bg-gradient-to-b from-yellow-300 via-primary to-yellow-600 bg-clip-text text-transparent drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">
            Wolf Cloud
          </h1>
          <p className="mt-4 text-lg md:text-xl max-w-2xl mx-auto text-white/90 whitespace-pre-line">
            {heroSubtitle}
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Link href="/wolfexpress">
                <Truck className="mr-2 h-5 w-5"/>
                Receba Agora Com Express
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/50 bg-black/20 hover:bg-white/10 text-white">
              <Link href="/roletadepremios">
                <Gift className="mr-2 h-5 w-5"/>
                Roleta da Sorte
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/50 bg-black/20 hover:bg-white/10 text-white">
              <Link href="/account/referrals">
                <Share2 className="mr-2 h-5 w-5"/>
                Indique e Ganhe!
              </Link>
            </Button>
             <Button asChild size="lg" variant="outline" className="border-white/50 bg-black/20 hover:bg-white/10 text-white">
              <Link href="/delivery-times">
                <Clock className="mr-2 h-5 w-5"/>
                Horários de Entrega
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Categories Icons */}
      <section className="container mx-auto px-4 py-16">
        <h3 className="text-3xl font-bold mb-2 text-center">Nossas Categorias</h3>
        <p className="text-muted-foreground text-center mb-10">Encontre exatamente o que você precisa.</p>
        <div className="flex flex-wrap justify-center gap-6 md:gap-8">
          {mainCategories.map((category) => {
            const IconComponent = iconMap[category.iconName] || ShoppingBag;
            return (
            <Link 
                key={category.id} 
                href={`/fastorder?category=${category.id}`}
                className="flex flex-col items-center group"
            >
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-transparent group-hover:border-primary transition-all duration-300 shadow-md relative bg-card">
                 <div className="w-full h-full flex items-center justify-center bg-card text-primary">
                    <IconComponent size={32} strokeWidth={1.5} />
                 </div>
              </div>
              <span className="mt-2 text-sm md:text-base font-medium text-center group-hover:text-primary transition-colors">
                {category.name}
              </span>
            </Link>
          )})}
        </div>
      </section>

      {/* Products by Category */}
      <div className="container mx-auto px-4 pb-12 space-y-12">
        {mainCategories.map((category) => {
            const categoryProducts = products.filter(p => p.category === category.name);
            
            if (categoryProducts.length === 0) return null;

            return (
                <section key={category.id} className="flex flex-col space-y-4">
                    <div className="flex items-center justify-between border-b pb-2 border-border/50">
                        <h3 className="text-2xl font-bold">{category.name}</h3>
                        <Link 
                            href={`/fastorder?category=${category.id}`} 
                            className={buttonVariants({ variant: "ghost", size: "sm", className: "gap-1" })}
                        >
                            Ver todos <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {categoryProducts.map((product) => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                </section>
            );
        })}
      </div>
    </div>
  );
}
